// START IMPORTS
import { useAppConfigContext } from '@ltvco/refresh-lib/ctx';
import { useReport } from '@ltvco/refresh-lib/hooks';
import { useDarkWebUpsell } from '@ltvco/refresh-lib/payments';
import { Stack, Box, useTheme } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
import {
  DebugMenu,
  ReportFactory,
  ReportLoading,
  useCreateDarkWeb,
  useDarkWebReport,
} from '@ltvco/refresh-lib/v1';
import {
  FraudEmailOverview,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  ReportNavigationMenuWithPermissions,
  ReportActionsWithDateUpdated,
  ReportRating,
  AdditionalDetailsSection,
  PotentialOwnerCta as PotentialOwnerCtaV2,
  ReportNullState,
  DarkWebBreachTips,
  DarkWebCoverage,
  DarkWebResults as DarkWebResultsV2,
  DarkWebScanFAQ as DarkWebFAQV2,
  EmailDarkWebVerify,
  FeatureBlockedModal,
  IReportMenuItemWithPermissions,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ReportRouteProps } from 'components/reports/types';
import { getFraudEmailNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';
// END IMPORTS

export function EmailFraudReportPage({ permalink }: ReportRouteProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const theme = useTheme();
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);
  const { trackEventGA4 } = useAppConfigContext();
  const { isDarkWebBlocked } = useDarkWebUpsell();
  const {
    isFeatureBlockedModalOpen,
    openFeatureBlockedModal,
    closeFeatureBlockedModal,
  } = useFeatureBlockedModal();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isDarkWebSectionLoading, setIsDarkWebSectionLoading] = useState(false);
  const [showDarkWebEmailSection, setShowDarkWebEmailSection] = useState(false);
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const isEmailFraudRedesign = useFeatureIsOn('RFRSH-4333');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const delayDarkWebLoading = 1000;
  const fallbackEmail =
    new URLSearchParams(search).get('email')?.toLowerCase() ?? '';
  const reportEmail =
    queryResult.data?.meta?.search_data?.email?.toLowerCase() ?? '';
  const darkWebEmail = isEmailFraudRedesign ? reportEmail || fallbackEmail : '';
  const { data: createDarkWebData } = useCreateDarkWeb({ email: darkWebEmail });
  const { data: darkWebData, isLoading: isDarkWebReportLoading } =
    useDarkWebReport({ email: darkWebEmail });
  const breaches = isEmailFraudRedesign
    ? darkWebData?.data?.breaches ?? []
    : [];
  const isDarkWebLoading = isDarkWebReportLoading || isDarkWebSectionLoading;
  const darkWebPermalink = createDarkWebData?.report?.permalink ?? permalink;
  const reportType = 'email-fraud';
  const zeroed = isZeroed(queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EVENT_HANDLERS
  function handleDarkWebSectionLoadingScreen() {
    setIsDarkWebSectionLoading(true);
    setShowDarkWebEmailSection(true);
    setTimeout(() => {
      setIsDarkWebSectionLoading(false);
    }, delayDarkWebLoading * 10);
  }
  // END EVENT_HANDLERS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const report = ReportFactory.create(queryResult?.data, 'email-fraud');

  const email = report.data.address?.toLowerCase() ?? fallbackEmail;
  document.title = `${email} - NumberGuru`;

  const fraudEmailNavLinkDataV2 = getFraudEmailNavLinkDataV2();
  const DARK_WEB_NAV_ITEM_NAME = 'dark-web-breaches-section';
  const navMenuItemsWithPermissions = (
    fraudEmailNavLinkDataV2 as IReportMenuItemWithPermissions[]
  ).map((item) =>
    item.name === DARK_WEB_NAV_ITEM_NAME ||
    item.sectionId === `#${DARK_WEB_NAV_ITEM_NAME}`
      ? {
          ...item,
          isLocked: isDarkWebBlocked,
          onLockedClick: () => {
            trackEventGA4({
              event_name: 'engagement_click',
              type: 'darkwebscan_reportsidebar',
              section_name: 'darkweb_upsell',
            });
            openFeatureBlockedModal();
          },
        }
      : item
  );

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  if (!report) {
    return <ReportLoading menuItems={2} />;
  }

  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }

  const {
    isCatchAll,
    isFrequentComplainer,
    activity,
    isLeaked,
    validityScore,
    isRecentAbuse,
    domain,
  } = report.data;

  const { isRiskyTld, isDmarcRecord, isDnsValid, mxRecords, suggestedDomain } =
    domain || {};

  const additionalDetails = (
    <AdditionalDetailsSection
      isRecentAbuse={isRecentAbuse}
      isDmarcRecord={isDmarcRecord}
      typo={suggestedDomain}
      isCatchAll={isCatchAll}
      isFrequentComplainer={isFrequentComplainer}
      isLeaked={isLeaked}
      isRiskyTld={isRiskyTld}
      isDnsValid={isDnsValid}
      validityScore={validityScore}
      userActivity={activity}
      mxRecords={mxRecords}
    />
  );
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenuWithPermissions
            reportType="Email Fraud"
            headerTitle={email}
            menuItems={navMenuItemsWithPermissions}
          />
        </Box>
        <Box marginTop={0} width="100%">
          <Box
            sx={{
              height: 30,
              backgroundColor: theme.palette.background.default,
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Fraud Scan"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <FraudEmailOverview
            reportData={report.data}
            breaches={breaches}
            loading={isDarkWebLoading}
          />

          <Stack spacing={2}>
            <PotentialOwnerCtaV2 email={email} reportType="email" />
            {isEmailFraudRedesign && (
              <>
                {additionalDetails}
                {!isDarkWebBlocked && (
                  <>
                    {showDarkWebEmailSection ? (
                      <DarkWebResultsV2
                        email={email}
                        breaches={breaches}
                        loading={isDarkWebLoading}
                        delayDuration={delayDarkWebLoading}
                        onFraudReport
                      />
                    ) : (
                      <EmailDarkWebVerify
                        email={email}
                        sectionTitle="Data Breaches"
                        sectionId="dark-web-breaches-section"
                        handleDarkWebLoadingScreen={
                          handleDarkWebSectionLoadingScreen
                        }
                      />
                    )}
                  </>
                )}
                <DarkWebBreachTips permalink={darkWebPermalink} noMonitoring />
                <DarkWebCoverage />
                <DarkWebFAQV2 />
              </>
            )}
            {!isEmailFraudRedesign && additionalDetails}
            <ReportRating
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
          </Stack>
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Fraud Scan"
        headerTitle={email}
        menuItems={
          fraudEmailNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
      />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType="Dark Web"
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </Box>
  );
  // END RENDER
}
